/**
 * OidcCallbackPage
 *
 * Implements the "Frontend-Initiated OIDC" pattern (oidc_frontend_guide.md):
 *
 *   1. Authentik redirects the browser to /signin/callback?code=...&state=...
 *   2. This page renders a loading screen and, in a useEffect, fires an AJAX
 *      request (credentials: include) to the backend /callback endpoint.
 *   3. The backend validates the code, sets the session cookie via Set-Cookie,
 *      and responds with JSON { status, redirect_to }.
 *   4. We read redirect_to from the JSON and navigate the browser there.
 *
 * The backend MUST return 200 OK + JSON (no 302). CORS must allow this origin
 * with Access-Control-Allow-Credentials: true.
 */

import { useEffect, useCallback, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { env, API_ENDPOINTS, ROUTES } from "@monorepo/config";
import { Button, ErrorBanner, useToast } from "@monorepo/ui";
import styles from "./OidcCallbackPage.module.scss";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------

type ExchangeStatus = "idle" | "loading" | "success" | "error";

// ----------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------

const PAGE_CONTENT = {
  LOADING: "Finalizing your authentication…",
  SUCCESS: "Success! Redirecting you now…",
  ERROR_GENERIC: "Authentication failed. Please try again.",
  ERROR_EXPIRED: "Your session link has expired. Please sign in again.",
  ERROR_NETWORK: "Network error. Please check your connection and retry.",
  RETRY_BUTTON: "Retry",
  // TITLE: "Completing Sign In",
} as const;

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------

export function OidcCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const { showToast } = useToast();

  const [exchangeStatus, setExchangeStatus] =
    useState<ExchangeStatus>("idle");
  const [statusMessage, setStatusMessage] = useState<string>("Processing your authentication...");
  const [isMobileFlow, setIsMobileFlow] = useState(false);

  // Track the OIDC params — they only exist for the lifetime of this URL.
  const code = searchParams.get("code");
  const state = searchParams.get("state");

  // Determine device ID immediately for UI — check sessionStorage first, then
  // localStorage (fallback for Flutter WebViews where sessionStorage may be
  // cleared after an external OIDC provider redirect), then the URL param.
  const deviceId = sessionStorage.getItem("oidc_auth_deviceid")
    || localStorage.getItem("oidc_auth_deviceid")
    || searchParams.get("deviceid");

  // ------------------------------------------------------------------
  // Core: perform the AJAX code exchange
  // ------------------------------------------------------------------
  const performExchange = useCallback(async () => {
    if (!code) {
      setExchangeStatus("error");
      setStatusMessage(PAGE_CONTENT.ERROR_EXPIRED);
      return;
    }

    setExchangeStatus("loading");
    setStatusMessage(PAGE_CONTENT.LOADING);

    try {
      // Build the callback URL.
      // Match sandbox exactly: only send code and state
      const params = new URLSearchParams();
      if (code) params.set("code", code);
      if (state) params.set("state", state);

      const callbackUrl = `${env.apiBaseUrl}${API_ENDPOINTS.OIDC_CALLBACK}?${params.toString()}`;

      // Use fetch with credentials: include to send the code to APISIX
      // Just like in the OIDC sandbox application.
      const response = await fetch(callbackUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        const target = data.backendRedirect || data.redirect_to;
        
        // ------------------------------------------------------------------
        // Mobile Handover Logic
        // ------------------------------------------------------------------
        if (deviceId) {
          setIsMobileFlow(true);
          setStatusMessage("Authentication confirmed. Finalizing mobile session...");
          
          try {
            // Call special backend endpoint for tokens
            const tokenUrl = `${env.apiBaseUrl}${API_ENDPOINTS.MOBILE_GET_ACCESS_TOKEN}`;
            const tokenResponse = await fetch(tokenUrl, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Accept': 'application/json' },
            });
            
            if (tokenResponse.ok) {
              const tokenData = await tokenResponse.json();
              const payload = { status: "ok", ...tokenData };
              const message = JSON.stringify({ type: "AUTH_SUCCESS", payload: payload });
              if ((window as any).FlutterJSChannel) {
                (window as any).FlutterJSChannel.postMessage(message);
              } else {
                window.postMessage(message, "*");
              }

              // Keep loading spinner spinning for seamless experience
              setExchangeStatus("loading");
              return; // Stop standard redirect
            } else {
              console.warn("Failed to fetch mobile access tokens");
              setExchangeStatus("error");
              setStatusMessage("Mobile session capture failed. Please try again or copy tokens below if available.");
              return; // Stop standard redirect
            }
          } catch (tokenErr) {
            console.error("Error during mobile token handover:", tokenErr);
            setExchangeStatus("error");
            setStatusMessage("A technical error occurred during mobile handover.");
            return; // Stop standard redirect
          }
        }

        // ------------------------------------------------------------------
        // Standard Browser Redirect
        // ------------------------------------------------------------------
        setExchangeStatus("success");
        setStatusMessage(PAGE_CONTENT.SUCCESS);

        if (target) {
          showToast("Login completed successfully!", "success");
          // Short delay to allow user to see the tick and toast
          setTimeout(() => {
            window.location.href = target;
          }, 1500);
        }
      } else {
        throw new Error("Failed to exchange code for session");
      }
    } catch (err: any) {
      if (isMounted.current) {
        if (deviceId) {
          setIsMobileFlow(true);
          setStatusMessage("Connection signal captured. Finalizing mobile session...");
          
          // Try to fetch tokens even if the initial exchange threw (common with CORS on 302s)
          try {
            const tokenUrl = `${env.apiBaseUrl}${API_ENDPOINTS.MOBILE_GET_ACCESS_TOKEN}`;
            const tokenResponse = await fetch(tokenUrl, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Accept': 'application/json' },
            });
            
            if (tokenResponse.ok) {
              const tokenData = await tokenResponse.json();
              const payload = { status: "ok", ...tokenData };
              const message = JSON.stringify({ type: "AUTH_SUCCESS", payload: payload });
              if ((window as any).FlutterJSChannel) {
                (window as any).FlutterJSChannel.postMessage(message);
              } else {
                window.postMessage(message, "*");
              }
              setExchangeStatus("loading");
              return;
            }
          } catch (e) {
            console.warn("Mobile fallback token fetch failed", e);
          }
          
          setExchangeStatus("error");
          setStatusMessage("Mobile session capture failed. No tokens available.");
          return;
        }

        // Standard Fallback logic (unchanged for web users)
        setExchangeStatus("success");
        setStatusMessage("Authenticated. Redirecting to application...");

        // Fallback jump if fetch failed due to CORS on the redirect.
        // We fetch the redirect JSON to follow the target URL programmatically.
        const handleFallback = async () => {
          try {
            const redirResponse = await fetch(`${env.apiBaseUrl}${API_ENDPOINTS.SIGNIN_REDIRECT}`, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Accept': 'application/json' },
            });
            if (redirResponse.ok) {
              const data = await redirResponse.json();
              
              // CRITICAL: If mobile, STOP if target is the dashboard
              if (deviceId) {
                 setExchangeStatus("error");
                 setStatusMessage("Mobile session capture failed. No tokens available.");
                 return;
              }

              if (data.redirect_to) {
                showToast("Session Captured. Redirecting...", "success");
                window.location.href = data.redirect_to;
                return;
              }
            }
          } catch (e) {
            console.warn("Fallback fetch failed, using direct jump");
          }
          
          if (!deviceId) {
            window.location.href = `${env.apiBaseUrl}${API_ENDPOINTS.SIGNIN_REDIRECT}`;
          } else {
            setExchangeStatus("error");
            setStatusMessage("Finalizing mobile session failed. Redirect blocked.");
          }
        };

        showToast("Authenticated. Finalizing...", "info");
        setTimeout(handleFallback, 1000);
      }
    } finally {
    }
  }, [code, state, deviceId, showToast]);

  // ------------------------------------------------------------------
  // Mount / unmount
  // ------------------------------------------------------------------

  useEffect(() => {
    isMounted.current = true;

    if (!code) {
      setExchangeStatus("error");
      setStatusMessage(PAGE_CONTENT.ERROR_EXPIRED);
    } else {
      // Auto-trigger exchange when page loads
      performExchange();
    }

    return () => {
      isMounted.current = false;
    };
  }, [code, performExchange]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className={styles.fullScreenMinimal}>
      <div className={styles.minimalContainer}>

        {exchangeStatus !== "idle" && (
          <div className={styles.statusDisplay}>
            {exchangeStatus === "loading" ? (
              <div className={styles.minimalSpinner} />
            ) : (
              <>
                {exchangeStatus === "success" && (
                  <div className={styles.minimalSuccessIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
                <p className={styles.successText}>
                  {exchangeStatus === "error" ? statusMessage : (isMobileFlow ? "Mobile session active" : "Successfully login completed")}
                </p>
              </>
            )}
          </div>
        )}


      </div>

      {exchangeStatus === "error" && (
        <div className={styles.errorView}>
          <ErrorBanner
            title="Exchange Failed"
            message={statusMessage}
          />
          <div className={styles.actionGroup}>
            <Button variant="primary" fullWidth onClick={performExchange}>
              {PAGE_CONTENT.RETRY_BUTTON}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate(ROUTES.SIGNIN, { replace: true })}>
              Back to Sign In
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
